import * as React from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { useForgotPassword } from "@/hooks/usePassword";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";

export function ForgotPasswordPage() {
  const { toast } = useToast();
  const forgot = useForgotPassword();

  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (forgot.isPending) return;
    try {
      await forgot.mutateAsync(email.trim());
      // The backend always returns a neutral message (it never reveals
      // whether the address exists), so we always show the "check inbox"
      // confirmation regardless of the response.
      setSent(true);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Something went wrong. Try again.";
      toast({ title: "Couldn't send reset link", description: message, variant: "destructive" });
    }
  }

  return (
    <AuthLayout
      eyebrow="Account recovery"
      title="Forgot your password?"
      sub="Enter your email and we'll send you a link to reset it."
    >
      <Card className="border-brand-border shadow-brand-md">
        {sent ? (
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <MailCheck className="h-6 w-6" />
            </div>
            <CardTitle className="text-xl">Check your inbox</CardTitle>
            <CardDescription className="max-w-xs">
              If an account exists for <span className="font-medium text-foreground">{email}</span>,
              a reset link is on its way. It expires in 15 minutes.
            </CardDescription>
            <Button asChild variant="outline" className="mt-2">
              <Link to="/login">
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </Button>
          </CardContent>
        ) : (
          <>
            <CardHeader className="pb-3">
              <CardTitle className="text-xl sm:text-2xl">Reset password</CardTitle>
              <CardDescription>We'll email you a secure link.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@company.com"
                  />
                </div>
                <Button type="submit" disabled={forgot.isPending} className="w-full" size="lg">
                  {forgot.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send reset link
                </Button>
              </form>
            </CardContent>
          </>
        )}
      </Card>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Remembered it?{" "}
        <Link to="/login" className="font-medium text-primary hover:underline">
          Back to sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
